import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Section, Reveal } from "@/components/network/NetworkAtoms";
import { PrimaryCta, SecondaryCta } from "./FundingCta";
import { RotateCcw, Sparkles } from "lucide-react";

/**
 * Three taps, then a route — and the answers carry straight into the application, so the form
 * opens with the facility type and size already set. Runs entirely in the browser until they apply.
 */
const QUESTIONS = [
  {
    k: "turnover", q: "What does the business turn over?",
    options: [
      { k: "sub1", label: "Under £1m" },
      { k: "mid", label: "£1m – £10m" },
      { k: "upper", label: "£10m – £50m" },
      { k: "large", label: "£50m+" },
    ],
  },
  {
    k: "amount", q: "What size of facility are you looking at?",
    options: [
      { k: "a", label: "£25k – £250k", floor: 25000 },
      { k: "b", label: "£250k – £1m", floor: 250000 },
      { k: "c", label: "£1m – £10m", floor: 1000000 },
      { k: "d", label: "£10m+", floor: 10000000 },
    ],
  },
  {
    k: "purpose", q: "And what is it for?",
    options: [
      { k: "working", label: "Working capital or tax" },
      { k: "equipment", label: "Plant, equipment or fleet" },
      { k: "invoices", label: "Money tied up in invoices" },
      { k: "property", label: "Property or development" },
      { k: "growth", label: "Acquisition or growth" },
    ],
  },
];

const FLOORS = { a: 25000, b: 250000, c: 1000000, d: 10000000 };

function suggest({ turnover, amount, purpose }) {
  const big = amount === "c" || amount === "d";
  if (purpose === "invoices") {
    return {
      headline: "Invoice finance, sized against your ledger",
      body: "Your customers are holding your working capital. A receivables facility advances the majority of each "
          + "invoice on issue and grows with turnover instead of capping out at a fixed limit.",
      also: big ? "At this size expect a confidential facility with a funder who knows your sector."
                : "Often live within a week of application.",
      type: "Invoice Finance",
    };
  }
  if (purpose === "equipment") {
    return {
      headline: "Asset finance, with the asset carrying the security",
      body: "Because the plant or vehicle secures the facility, appetite holds where unsecured lending thins out — "
          + "and the term is matched to the working life of what you're buying.",
      also: big ? "Larger tickets bring a valuation and a slightly longer run-in." : "Commonly decided inside 24 hours.",
      type: "Asset Finance",
    };
  }
  if (purpose === "property") {
    return {
      headline: big ? "Development or term debt secured on property" : "Bridging or secured property finance",
      body: "Secured against the asset, underwritten on the exit. Bring the exit — sale, refinance or term facility — "
          + "and this moves at the speed of the valuation rather than the speed of a credit committee.",
      also: "Specialist territory, and where a panel earns its keep.",
      type: big ? "Property Development Finance" : "Bridging",
    };
  }
  if (purpose === "working") {
    return {
      headline: big ? "A revolving facility or structured term debt" : "Term debt, or a dedicated tax facility",
      body: "Short-dated liabilities are cheapest on a facility built for them. Longer working capital needs suit a "
          + "term structure you can plan around, or a revolver you draw and repay as trading demands.",
      also: "If customers are also slow to pay, ask about invoice finance alongside it.",
      type: big ? "Revolving Credit Facility" : "VAT Loan",
    };
  }
  if (turnover === "sub1" && !big) {
    return {
      headline: "Growth funding, sized to the trading record",
      body: "With a shorter track record the market narrows but stays open: revenue-based facilities, asset finance "
          + "and government-backed lending all sit here. We'll tell you what is realistic before you commit time.",
      also: "Clear answers on day one, including where the answer is no.",
      type: "Business Finance",
    };
  }
  return {
    headline: "Acquisition or structured growth debt",
    body: "Senior, unitranche and mezzanine capital for buy-outs, bolt-ons and shareholder reorganisations — placed "
        + "with clearing banks, debt funds and private credit, including club and syndicated positions.",
    also: big ? "At this level we would normally structure two instruments rather than force one."
              : "Often blended with an asset or receivables line.",
    type: "Growth Debt",
  };
}

export default function EligibilityTeaser() {
  const reduce = useReducedMotion();
  const [answers, setAnswers] = useState({});
  const step = QUESTIONS.findIndex((q) => !answers[q.k]);
  const done = step === -1;
  const result = done ? suggest(answers) : null;

  const pick = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));
  const reset = () => setAnswers({});

  return (
    <Section testId="funding-eligibility" className="!py-20 sm:!py-24">
      <div className="relative mx-auto max-w-3xl">
        <div aria-hidden className="absolute -inset-x-10 -inset-y-8 rounded-[2.5rem] bg-[#0055FF] opacity-[0.09] blur-[100px]" />
        <Reveal>
          <div className="relative rounded-3xl border border-white/12 bg-[#0B1220]/70 backdrop-blur-xl p-7 sm:p-10">
            <div className="flex items-center gap-2.5">
              <Sparkles className="size-4 text-[#7da9ff]" />
              <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[#7da9ff]">
                Which route fits
              </span>
            </div>

            {!done ? (
              <>
                <div className="mt-6 flex items-center gap-1.5" data-testid="eligibility-progress">
                  {QUESTIONS.map((q, i) => (
                    <span key={q.k} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
                      i < step ? "bg-[#0055FF]" : i === step ? "bg-[#7da9ff]/50" : "bg-white/10"}`} />
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div key={QUESTIONS[step].k}
                              initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.35 }}>
                    <h2 className="mt-7 font-cabinet text-[26px] sm:text-[32px] leading-tight tracking-[-0.03em] font-semibold text-white">
                      {QUESTIONS[step].q}
                    </h2>
                    <div className="mt-6 flex flex-wrap gap-2.5">
                      {QUESTIONS[step].options.map((o) => (
                        <button key={o.k} onClick={() => pick(QUESTIONS[step].k, o.k)}
                                data-testid={`eligibility-${QUESTIONS[step].k}-${o.k}`}
                                className="rounded-full border border-white/15 bg-white/[0.04] hover:border-[#0055FF] hover:bg-[#0055FF]/15 px-5 h-11 text-[14px] text-white transition-colors">
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                </AnimatePresence>

                <p className="mt-8 text-[12px] text-slate-500 leading-relaxed">
                  This runs in your browser. Nothing is sent, nothing is stored and no credit file is touched —
                  it simply sets up your application so you aren't starting from a blank page.
                </p>
              </>
            ) : (
              <motion.div initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.45 }} data-testid="eligibility-result">
                <h2 className="mt-6 font-cabinet text-[26px] sm:text-[34px] leading-tight tracking-[-0.03em] font-semibold text-white">
                  {result.headline}
                </h2>
                <p className="mt-4 text-[15.5px] leading-relaxed text-slate-300">{result.body}</p>
                <p className="mt-3 text-[14px] leading-relaxed text-[#7da9ff]">{result.also}</p>

                <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <PrimaryCta testId="eligibility-cta"
                              label="Continue into your application"
                              params={{ type: result.type, amount: String(FLOORS[answers.amount] || "") }} />
                  <SecondaryCta testId="eligibility-desk" label="Read the detail first" target="funding-products" />
                  <button onClick={reset} data-testid="eligibility-reset"
                          className="inline-flex items-center justify-center gap-2 h-14 px-4 text-slate-400 hover:text-white text-[13.5px] transition-colors">
                    <RotateCcw className="size-3.5" /> Start again
                  </button>
                </div>

                <p className="mt-7 text-[12px] text-slate-500 leading-relaxed">
                  Your answers carry into the form — facility type and size are pre-set, and you can change either.
                  A steer, not a decision: what is achievable depends on the accounts, the security and the case.
                </p>
              </motion.div>
            )}
          </div>
        </Reveal>
      </div>
    </Section>
  );
}
